import { backgroundOrange, orange, pdfBadgeBg, pdfBadgeFg } from '@/theme/colors'
import styled from 'styled-components'

export const FilesPageContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	background-color: ${backgroundOrange};
	overflow-y: auto;
`

export const FilesContent = styled.div`
	/* The 2.5rem top matches the overview's header→title breathing room. */
	padding: 2.5rem 1.5rem 2rem;
	max-width: 1400px;
	margin: 0 auto;
	width: 100%;

	@media (max-width: 600px) {
		padding: 1rem 0.875rem;
	}
`

export const FilesHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 1.5rem;
	gap: 1rem;
	flex-wrap: wrap;

	@media (max-width: 600px) {
		margin-bottom: 1rem;
		gap: 0.75rem;
	}
`

export const UploadButton = styled.button`
	background: ${orange};
	color: white;
	border: none;
	padding: 0.625rem 1.25rem;
	border-radius: 8px;
	font-size: 0.875rem;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	gap: 0.5rem;

	&:hover:not(:disabled) {
		opacity: 0.9;
		transform: translateY(-1px);
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`

export const FileGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
	gap: 1rem;

	@media (max-width: 600px) {
		grid-template-columns: repeat(2, 1fr);
		gap: 0.75rem;
	}

	@media (min-width: 768px) {
		grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
	}

	@media (min-width: 1200px) {
		grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
	}
`

export const FileCard = styled.div`
	background: white;
	border-radius: 10px;
	overflow: hidden;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
	cursor: pointer;
	transition: all 0.2s ease;
	position: relative;

	&:hover {
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		transform: translateY(-2px);
	}
`

/** $pdf paints the app-wide red PDF badge; other non-images stay neutral grey. */
export const FileThumbnail = styled.div<{ $pdf?: boolean }>`
	width: 100%;
	aspect-ratio: 1;
	overflow: hidden;
	background: ${({ $pdf }) => ($pdf ? pdfBadgeBg : '#f3f4f6')};
	display: flex;
	align-items: center;
	justify-content: center;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
`

export const FileTypeIcon = styled.div<{ $pdf?: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.5rem;
	color: ${({ $pdf }) => ($pdf ? pdfBadgeFg : '#9ca3af')};

	span {
		font-size: 0.75rem;
		font-weight: 600;
		text-transform: uppercase;
		color: ${({ $pdf }) => ($pdf ? pdfBadgeFg : '#6b7280')};
	}
`

export const FileInfo = styled.div`
	padding: 0.625rem 0.75rem;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.5rem;
`

export const FileName = styled.div`
	font-size: 0.8rem;
	font-weight: 500;
	color: #374151;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	flex: 1;
	min-width: 0;
`

export const DeleteFileButton = styled.button`
	background: transparent;
	border: none;
	color: #9ca3af;
	padding: 4px;
	border-radius: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: color 0.2s;
	flex-shrink: 0;

	&:hover {
		color: #dc2626;
	}
`

export const EmptyState = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 4rem 2rem;
	text-align: center;
	color: #6b7280;
	gap: 1rem;

	p {
		margin: 0;
		font-size: 1rem;
	}

	span {
		font-size: 0.875rem;
		color: #9ca3af;
	}
`

export const LoadingContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 1;
	font-size: 18px;
	color: #6b7280;
`

export const ErrorContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 1;
	font-size: 18px;
	color: #dc2626;
`

export const UploadOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.6);
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	z-index: 999;
	gap: 1rem;
	color: white;
	backdrop-filter: blur(2px);
`

export const UploadSpinner = styled.div`
	width: 48px;
	height: 48px;
	border: 4px solid rgba(255, 255, 255, 0.3);
	border-top-color: white;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;

	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}
`

export const UploadText = styled.div`
	font-size: 1.125rem;
	font-weight: 500;
`

export const HiddenFileInput = styled.input`
	display: none;
`
